import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Sparkles, Coins, ChevronRight } from "lucide-react";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePageMeta, eur } from "@/lib/format";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";

const RETURN_URL = "/app/membership";

const TIER_META = {
  plus: { nameKey: "tier.plus", blurbKey: "tier.plus_blurb" },
  pro: { nameKey: "tier.pro", blurbKey: "tier.pro_blurb" },
  unlimited: { nameKey: "tier.unlimited", blurbKey: "tier.unlimited_blurb" },
  coach_pro: { nameKey: "tier.coach_pro", blurbKey: "tier.coach_pro_blurb" },
};

function features(t, tr) {
  const lines = [];
  if (tr.reports_quota >= 9999) lines.push(t("feat.reports_unlimited"));
  else lines.push(t("feat.reports_count", { n: tr.reports_quota }));
  if (!tr.ads) lines.push(t("feat.ad_free"));
  if (tr.voice_journal) lines.push(t("feat.voice_journal"));
  if (tr.starter_plans) lines.push(t("feat.starter_plans"));
  if (tr.priority) lines.push(t("feat.priority"));
  if (tr.coach_commission_pct != null) lines.push(t("feat.commission", { pct: Math.round(tr.coach_commission_pct * 100) }));
  if (tr.coach_advanced_analytics) lines.push(t("feat.advanced_analytics"));
  return lines;
}

const baseOf = (tier) => (tier || "free").replace("_yearly", "");

export default function Membership() {
  const t = useT();
  usePageMeta(t("mem.title"));
  const { user, refreshUser } = useAuth();
  const [tiers, setTiers] = useState(null);
  const [mine, setMine] = useState(null);
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState(null);
  const [managing, setManaging] = useState(false);

  const isCoach = user?.role === "coach";

  useEffect(() => {
    api.get("/subscription/tiers").then((r) => setTiers(r.data || {})).catch(() => setTiers({}));
    api.get("/subscription/mine").then((r) => setMine(r.data || {})).catch(() => setMine({}));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("sub") === "success") {
      toast.success(t("bill.sub_activated"));
      refreshUser().catch(() => {});
      api.get("/subscription/mine").then((r) => setMine(r.data || {})).catch(() => {});
      window.history.replaceState(null, "", RETURN_URL);
    } else if (params.get("sub") === "cancel") {
      toast(t("bill.cancelled"));
      window.history.replaceState(null, "", RETURN_URL);
    }
  }, [refreshUser]);

  const subscribe = async (tierKey) => {
    setBusy(tierKey);
    try {
      const { data } = await api.post("/subscribe/app", {
        tier: tierKey,
        payment_mode: "stripe",
        origin_url: window.location.origin + RETURN_URL,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.success(t("bill.activated"));
      await refreshUser();
      const r = await api.get("/subscription/mine");
      setMine(r.data || {});
    } catch (err) {
      toast.error(errMessage(err, t("bill.finish_failed")));
    } finally {
      setBusy(null);
    }
  };

  const manageSub = async () => {
    setManaging(true);
    try {
      const { data: info } = await api.get("/subscription/cancel-info");
      if (info.source === "stripe") {
        const { data } = await api.post("/subscription/cancel-portal", { return_url: window.location.origin + RETURN_URL });
        if (data?.url && !data.mock) { window.location.href = data.url; return; }
        toast(t("bill.manage_demo"));
      } else if (info.source === "apple") {
        window.open(info.deep_link_ios, "_blank");
      } else if (info.source === "google") {
        window.open(info.deep_link_android, "_blank");
      } else {
        toast(info.message || t("bill.no_active_sub"));
      }
    } catch (err) { toast.error(errMessage(err, t("bill.manage_failed"))); }
    finally { setManaging(false); }
  };

  const bases = isCoach ? ["plus", "pro", "unlimited", "coach_pro"] : ["plus", "pro", "unlimited"];
  const currentBase = baseOf(mine?.tier);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl text-foreground">{t("mem.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("bill.membership_sub")}
        </p>
      </div>

      {/* Current plan clarity (§19) */}
      {mine === null ? (
        <Skeleton className="h-20 rounded-2xl" />
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5" data-testid="mem-current-plan">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">{t("bill.membership")}</p>
            <p className="mt-0.5 font-display text-xl text-foreground" data-testid="mem-current-tier">
              {currentBase && currentBase !== "free"
                ? (TIER_META[currentBase] ? t(TIER_META[currentBase].nameKey) : currentBase)
                : t("tier.free")}
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {mine?.is_lifetime
                ? t("rw3.mem_lifetime")
                : currentBase && currentBase !== "free"
                  ? t("bill.manage_hint")
                  : t("rw3.mem_free_access")}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Link to="/app/credits" data-testid="mem-credits-chip" className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent/50">
              <Coins className="h-4 w-4 text-sage-light" /> {t("bill.n_credits", { n: user?.credits ?? 0 })}
            </Link>
            {currentBase && currentBase !== "free" && !mine?.is_lifetime ? (
              <Button variant="outline" className="rounded-full" disabled={managing} onClick={manageSub} data-testid="mem-manage-sub">
                {managing ? <Loader2 className="h-4 w-4 animate-spin" /> : t("bill.manage")}
              </Button>
            ) : null}
          </div>
        </div>
      )}

      <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="interval-toggle">
        {[["month", t("bill.monthly")], ["year", t("bill.yearly")]].map(([v, label]) => (
          <button
            key={v}
            onClick={() => setInterval(v)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${interval === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            data-testid={`interval-${v}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tiers === null ? (
        <div className="grid gap-5 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-96 rounded-2xl" />)}
        </div>
      ) : (
        <div className={`grid gap-5 ${bases.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
          {bases.map((base) => {
            const key = interval === "year" ? `${base}_yearly` : base;
            const tr = tiers[key] || tiers[base];
            if (!tr) return null;
            const meta = TIER_META[base];
            const isCurrent = currentBase === base && currentBase !== "free";
            const highlight = base === "unlimited";
            return (
              <div key={base} className={`relative flex flex-col rounded-2xl border bg-card p-6 ${highlight ? "border-primary ring-1 ring-primary" : "border-border"}`}>
                {highlight && <Badge className="absolute -top-2.5 left-6 rounded-full"><Sparkles className="mr-1 h-3 w-3" /> {t("bill.recommended")}</Badge>}
                <h3 className="font-display text-2xl text-foreground">{t(meta.nameKey)}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{t(meta.blurbKey)}</p>
                <div className="mt-4">
                  <span className="font-display text-3xl font-semibold text-foreground">{eur(tr.price_eur_cents)}</span>
                  <span className="text-sm text-muted-foreground"> / {interval === "year" ? t("bill.per_year") : t("bill.per_month")}</span>
                </div>
                <ul className="mt-5 flex-1 space-y-2.5">
                  {features(t, tr).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-foreground/90">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  className="mt-6 w-full rounded-full"
                  variant={highlight ? "default" : "outline"}
                  disabled={isCurrent || !!busy}
                  onClick={() => subscribe(key)}
                  data-testid={`subscribe-${base}`}
                >
                  {busy === key ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrent ? t("bill.current") : t("bill.choose")}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <Link to="/app/billing" data-testid="mem-billing-link" className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4 transition-colors hover:bg-accent/40">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{t("rw3.mem_credits_title")}</p>
          <p className="mt-0.5 text-sm text-muted-foreground">{t("rw3.mem_credits_note")}</p>
        </div>
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
      </Link>

      {isCoach ? <p className="text-xs text-muted-foreground">{t("rw3.mem_coach_note")}</p> : null}

      <p className="text-xs text-muted-foreground">
        {t("bill.stripe_note_vat")}
      </p>
    </div>
  );
}
